import { Dialog, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import InputField from "../../../components/InputField";
import Label from "../../../components/label";
import DialogHeader from "../../../components/dialog/DialogHeader";
import DialogBody from "../../../components/dialog/DialogBody";
import DialogActionButtons from "../../../components/dialog/DialogActions";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { changePasswordValidation } from "../../../utils/validation";
import { t } from "i18next";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing("24px"),
  },
  "& .MuiPaper-root": {
    width: "500px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));

let paramsData = {};

const ChangePasswordDialog = forwardRef(({ handleConfirm }, ref) => {
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState({
    previous: true,
    new: true,
    reEnter: true,
  });

  const [inputValues, setInputValues] = useState({
    newPassword: "",
    reEnterPassword: "",
    oldPassword: "",
  });

  const [inputError, setInputError] = useState({
    newPassword: false,
    isPasswordStrong: false,
    reEnterPassword: false,
    isPasswordMatch: false,
    oldPassword: false,
  });

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      paramsData = params;
      setOpen(true);
    },
    closeDialog() {
      setOpen(false);
    },
  }));

  const handleClose = () => {
    setOpen(false);
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  const handleChangeInputs = (event) => {
    const { name, value } = event.target;
    setInputError((prevErr) => ({
      ...prevErr,
      [name]: false,
      isPasswordStrong:
        name === "newPassword" ? false : inputError.isPasswordStrong,
      isPasswordMatch:
        name === "reEnterPassword" ? false : inputError.isPasswordMatch,
    }));
    setInputValues((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <BootstrapDialog
      onClose={handleClose}
      aria-labelledby="customized-dialog-title"
      open={open}
    >
      <DialogHeader title={"changePassword"} onClose={handleClose} />
      <DialogBody>
        <Grid container rowSpacing={2}>
          <Grid item xs={12}>
            <Label
              text={t("previousPassword")}
              required
              htmlFor="user_previous_enter"
            />
            <InputField
              id={"user_previous_enter"}
              placeholder={`${t("enter")} ${t("previousPassword")}`}
              type={showPassword.previous ? "password" : "text"}
              InputEndIcon={
                showPassword.previous ? (
                  <VisibilityOffIcon
                    sx={{
                      cursor: "pointer",
                    }}
                    onClick={() => togglePasswordVisibility("previous")}
                  />
                ) : (
                  <VisibilityIcon
                    sx={{
                      cursor: "pointer",
                    }}
                    onClick={() => togglePasswordVisibility("previous")}
                  />
                )
              }
              inputError={inputError.oldPassword}
              helperText={
                inputError.oldPassword && t("previousPasswordRequired")
              }
              name="oldPassword"
              value={inputValues.oldPassword}
              onChange={handleChangeInputs}
            />
          </Grid>
          <Grid item xs={12}>
            <Label text={t("newPassword")} required htmlFor="user_new_pass" />
            <InputField
              id={"user_new_pass"}
              placeholder={`${t("enter")} ${t("newPassword")}`}
              type={showPassword.new ? "password" : "text"}
              InputEndIcon={
                showPassword.new ? (
                  <VisibilityOffIcon
                    sx={{
                      cursor: "pointer",
                    }}
                    onClick={() => togglePasswordVisibility("new")}
                  />
                ) : (
                  <VisibilityIcon
                    sx={{
                      cursor: "pointer",
                    }}
                    onClick={() => togglePasswordVisibility("new")}
                  />
                )
              }
              inputError={inputError.newPassword || inputError.isPasswordStrong}
              helperText={
                inputError.newPassword
                  ? t("newPasswordRequired")
                  : inputError.isPasswordStrong
                  ? t("passwordValidationDesc")
                  : null
              }
              name="newPassword"
              value={inputValues.newPassword}
              onChange={handleChangeInputs}
            />
          </Grid>
          <Grid item xs={12}>
            <Label
              text={t("reEnterPassword")}
              required
              htmlFor="user_new_pass_re_enter"
            />
            <InputField
              id={"user_new_pass_re_enter"}
              placeholder={`${t("reEnter")} ${t("newPassword")}`}
              type={showPassword.reEnter ? "password" : "text"}
              InputEndIcon={
                showPassword.reEnter ? (
                  <VisibilityOffIcon
                    sx={{
                      cursor: "pointer",
                    }}
                    onClick={() => togglePasswordVisibility("reEnter")}
                  />
                ) : (
                  <VisibilityIcon
                    sx={{
                      cursor: "pointer",
                    }}
                    onClick={() => togglePasswordVisibility("reEnter")}
                  />
                )
              }
              inputError={
                inputError.reEnterPassword || inputError.isPasswordMatch
              }
              helperText={
                inputError.reEnterPassword
                  ? `${t("reEnter")} ${t("newPassword")}`
                  : inputError.isPasswordMatch
                  ? t('passwordNotMatch')
                  : null
              }
              name="reEnterPassword"
              value={inputValues.reEnterPassword}
              onChange={handleChangeInputs}
            />
          </Grid>
        </Grid>
      </DialogBody>
      <DialogActionButtons
        onCancel={handleClose}
        onConfirm={() => {
          const validate = changePasswordValidation(inputValues, setInputError);
          if (!validate) {
            handleConfirm(inputValues);
          }
        }}
      />
    </BootstrapDialog>
  );
});

export default ChangePasswordDialog;
